<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { useApiClient } from "@/api/client";
import { t } from "@/plugins/i18n";
import { useBaseStore } from "@/stores/base";
import { toastApiResult } from "@/toaster";
import MasterPriceSimpleDO = Modules.BookingExperts.Data.MasterPriceList.MasterPriceSimpleDO;
import MasterPriceComparisonDO = Modules.BookingExperts.Data.MasterPriceList.MasterPriceComparisonDO;
import Modal from "@/ui/components/modal.vue";
import useDialog from "@/ui/composables/useDialog";

interface Props {
	modelValue: boolean;
}

interface Emits {
	(e: "update:modelValue", value: boolean): void;

	(e: "sync-completed"): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const store = useBaseStore();
const dialog = useDialog();
const api = useApiClient("bookingExperts");

// State
const loading = ref(false);
const saving = ref(false);
const pricelistData = ref<MasterPriceComparisonDO | null>(null);
const selectedToCreate = ref<Record<number, boolean>>({});
const selectedToRemove = ref<Record<number, boolean>>({});
const searchCreate = ref("");
const searchRemove = ref("");

// Computed
const isOpen = computed({
	get: () => props.modelValue,
	set: (value) => emit("update:modelValue", value),
});

// Generic filter function for both lists
const filterItems = (items: MasterPriceSimpleDO[] = [], searchTerm: string) => {
	if (!searchTerm) return items;
	const lowercaseTerm = searchTerm.toLowerCase();
	return items.filter((item) =>
		item.name?.toLowerCase().includes(lowercaseTerm)
	);
};

const filteredToCreate = computed(() =>
	filterItems(pricelistData.value?.create || [], searchCreate.value)
);

const filteredToRemove = computed(() =>
	filterItems(pricelistData.value?.remove || [], searchRemove.value)
);

// Count selected items
const createItemsCount = computed(
	() => Object.values(selectedToCreate.value).filter(Boolean).length
);

const removeItemsCount = computed(
	() => Object.values(selectedToRemove.value).filter(Boolean).length
);

const hasChanges = computed(
	() => createItemsCount.value > 0 || removeItemsCount.value > 0
);

const allowSubmit = computed(
	() => hasChanges.value && !loading.value && !saving.value
);

// Single headers definition for both tables
const tableHeaders = [
	{
		title: "",
		key: "selection",
		sortable: false,
		width: 40,
		align: "center" as const,
	},
	{ title: t("pricelist.index.table.headers.name"), key: "name" },
];

// Methods
async function fetchSyncData() {
	if (!isOpen.value) return;
	loading.value = true;
	selectedToCreate.value = {};
	selectedToRemove.value = {};

	try {
		const result = await toastApiResult(
			api.syncGet(store.selectedBexAdministration.id)
		);

		if (result.isOk()) {
			pricelistData.value = result.value;

			// Pre-select all items by default
			result.value.create.forEach(
				(item) => (selectedToCreate.value[item.id] = true)
			);
			result.value.remove.forEach(
				(item) => (selectedToRemove.value[item.id] = true)
			);
		}
	} catch (error) {
		console.error("Error fetching sync data:", error);
	} finally {
		loading.value = false;
	}
}

// Toggle all items for a section
function toggleSelection(
	items: MasterPriceSimpleDO[],
	target: Record<number, boolean>,
	value: boolean
) {
	items.forEach((item) => (target[item.id] = value));
}

async function applySyncChanges() {
	const confirm = await dialog.confirm({
		title: "Apply Changes",
		message: "Are you sure you want to apply these changes?",
	});

	if (!confirm) return;

	saving.value = true;

	try {
		const payload: MasterPriceComparisonDO = {
			create: pricelistData.value.create
				.filter((p) => selectedToCreate.value[p.id])
				.map((p) => ({ id: p.id, name: p.name })),
			remove: pricelistData.value.remove
				.filter((p) => selectedToRemove.value[p.id])
				.map((p) => ({ id: p.id, name: p.name })),
		};

		await toastApiResult(
			api.syncSet(store.selectedBexAdministration.id, payload)
		);

		emit("sync-completed");
		isOpen.value = false;
	} catch (error) {
		console.error("Error applying sync changes:", error);
	} finally {
		saving.value = false;
	}
}

// Watch for dialog open/close
watch(isOpen, (newVal) => {
	if (newVal) fetchSyncData();
});
</script>

<template>
	<Modal
		v-model:visible="isOpen"
		v-model:allow-submit="allowSubmit"
		:title="t('pricelist.sync.dialog_title')"
		icon="fa-light fa-sync"
		width="1100px"
		@submit="applySyncChanges"
	>
		<!-- Main Content -->
		<div v-if="loading" class="d-flex justify-center align-center my-8">
			<v-progress-circular indeterminate color="primary" />
		</div>

		<v-row v-else class="ma-0">
			<!-- TO CREATE Section -->
			<v-col cols="6" class="pa-2">
				<v-sheet rounded="lg" class="border" elevation="0">
					<v-sheet
						class="pa-3 d-flex align-center rounded-t-lg"
						color="success"
						bg-opacity="0.05"
					>
						<v-icon icon="fa-light fa-plus" size="small" class="mr-2" />
						<span class="text-subtitle-2 font-weight-medium">{{
							t("pricelist.sync.to_create")
						}}</span>
						<v-chip size="x-small" variant="tonal" class="ml-2"
							>{{ createItemsCount }}
						</v-chip>
						<v-spacer />
						<v-tooltip location="top">
							<template v-slot:activator="{ props }">
								<v-icon v-bind="props">mdi-help-circle-outline</v-icon>
							</template>
							<span>
								Pricelists marked to create result in new pricelists being
								imported and processed inside the application.
							</span>
						</v-tooltip>
					</v-sheet>

					<div class="pa-2 d-flex align-center border-b">
						<v-text-field
							v-model="searchCreate"
							prepend-inner-icon="mdi-magnify"
							hide-details
							density="compact"
							placeholder="Search"
							class="mr-2"
						/>
						<v-btn-group density="compact" variant="text">
							<v-btn
								icon
								width="32"
								size="small"
								color="success"
								variant="tonal"
								@click="
									toggleSelection(filteredToCreate, selectedToCreate, true)
								"
								:disabled="!filteredToCreate.length"
								:title="t('pricelist.sync.selectAll')"
							>
								<v-icon icon="fa-light fa-check-double" size="small" />
							</v-btn>
							<v-btn
								icon
								width="32"
								size="small"
								color="error"
								variant="tonal"
								@click="
									toggleSelection(filteredToCreate, selectedToCreate, false)
								"
								:disabled="!filteredToCreate.length"
								:title="t('pricelist.sync.deselectAll')"
							>
								<v-icon icon="fa-light fa-xmark" size="small" />
							</v-btn>
						</v-btn-group>
					</div>

					<v-data-table
						:headers="tableHeaders"
						:items="filteredToCreate"
						density="compact"
						class="border-0"
						hover
						:no-data-text="t('common.no_data')"
						height="340px"
						fixed-header
					>
						<template #item.selection="{ item }">
							<v-checkbox
								v-model="selectedToCreate[item.id]"
								hide-details
								density="compact"
								color="success"
							/>
						</template>
						<template #item.name="{ item }">
							<div class="font-weight-medium">{{ item.name }}</div>
						</template>
					</v-data-table>
				</v-sheet>
			</v-col>

			<!-- TO REMOVE Section -->
			<v-col cols="6" class="pa-2">
				<v-sheet rounded="lg" class="border" elevation="0">
					<v-sheet
						class="pa-3 d-flex align-center rounded-t-lg"
						color="error"
						bg-opacity="0.05"
					>
						<v-icon icon="fa-light fa-trash" size="small" class="mr-2" />
						<span class="text-subtitle-2 font-weight-medium">{{
							t("pricelist.sync.to_remove")
						}}</span>
						<v-chip size="x-small" variant="tonal" class="ml-2"
							>{{ removeItemsCount }}
						</v-chip>
						<v-spacer />
						<v-tooltip location="top">
							<template v-slot:activator="{ props }">
								<v-icon v-bind="props">mdi-help-circle-outline</v-icon>
							</template>
							<span>
								Pricelists marked to delete result in new pricelists being
								removed inside the application. Does *not* delete pricelists
								from Booking Experts!
							</span>
						</v-tooltip>
					</v-sheet>

					<div class="pa-2 d-flex align-center border-b">
						<v-text-field
							v-model="searchRemove"
							prepend-inner-icon="mdi-magnify"
							hide-details
							density="compact"
							placeholder="Search"
							class="mr-2"
						/>
						<v-btn-group density="compact" variant="text">
							<v-btn
								icon
								width="32"
								size="small"
								color="success"
								variant="tonal"
								@click="
									toggleSelection(filteredToRemove, selectedToRemove, true)
								"
								:disabled="!filteredToRemove.length"
								:title="t('pricelist.sync.selectAll')"
							>
								<v-icon icon="fa-light fa-check-double" size="small" />
							</v-btn>
							<v-btn
								icon
								width="32"
								size="small"
								color="error"
								variant="tonal"
								@click="
									toggleSelection(filteredToRemove, selectedToRemove, false)
								"
								:disabled="!filteredToRemove.length"
								:title="t('pricelist.sync.deselectAll')"
							>
								<v-icon icon="fa-light fa-xmark" size="small" />
							</v-btn>
						</v-btn-group>
					</div>

					<v-data-table
						:headers="tableHeaders"
						:items="filteredToRemove"
						density="compact"
						class="border-0"
						hover
						:no-data-text="t('common.no_data')"
						height="340px"
						fixed-header
					>
						<template #item.selection="{ item }">
							<v-checkbox
								v-model="selectedToRemove[item.id]"
								hide-details
								density="compact"
								color="error"
							/>
						</template>
						<template #item.name="{ item }">
							<div class="font-weight-medium">{{ item.name }}</div>
						</template>
					</v-data-table>
				</v-sheet>
			</v-col>
		</v-row>

		<v-card-text class="pa-2">
			<v-alert type="info" variant="tonal" density="compact" border="start">
				<p class="text-body-2 mb-0">
					Synchronize pricelists between Booking Experts and ProRevenue. The
					left panel shows pricelists that can be created. The right panel shows
					pricelists that can be removed. <i>Removed</i> Pricelists are
					<b><u>not deleted</u></b> from Booking Experts. Select or deselect
					items in each panel, then click "Apply Changes" to synchronize.
				</p>
			</v-alert>
		</v-card-text>

		<!-- Custom Actions Slot -->
		<template #actions>
			<v-btn
				color="error"
				variant="text"
				size="small"
				@click="isOpen = false"
				:disabled="saving"
			>
				{{ t("common.cancel") }}
			</v-btn>
			<v-btn
				color="info"
				variant="text"
				size="small"
				@click="fetchSyncData"
				:loading="saving"
				:disabled="saving"
			>
				{{ t("common.refresh") }}
			</v-btn>
			<v-btn
				color="primary"
				variant="tonal"
				size="small"
				:loading="saving"
				:disabled="!allowSubmit"
				@click="applySyncChanges"
			>
				<v-icon icon="fa-light fa-sync" size="small" class="mr-1" />
				{{ t("pricelist.sync.apply_changes") }}
			</v-btn>
		</template>
	</Modal>
</template>
